import { useState } from 'react'

import { Modal, message } from 'antd'

import { trackEvent } from 'common/analytics/event_tracking'
import { Events } from 'common/analytics/events'

import { Order } from 'vendor/server/public/orders'

export type StateChanges = {
  confirmed_at?: string
  rejected_at?: string
  cancellation_rejected_at?: string
  cancellation_accepted_at?: string
}

export type UploaderStore = {
  checkIfAllUploadsCompleted: () => boolean
  signedIds: (key: string) => string[]
  deleteAttachmentIds: { [key: string]: string[] }
}

export type UpdateAction = (stateChanges: StateChanges, uploaderStore: UploaderStore) => Promise<void>

const oldOrderVersionErrorMessage =
  'You are trying to save an order that has been recently updated by the customer. Please refresh the page and try again; unfortunately your changes will not be saved.'

export function useOrderAsyncAction(action: UpdateAction, selectedOrder: Nullable<Order>) {
  // This hook can also be used in private order detail pages
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  const execute = async (
    stateChanges: StateChanges,
    uploaderStore: UploaderStore,
    successMessage = 'Successfully saved changes',
    errorMessage = 'Unable to save updates to the order',
  ) => {
    if (!uploaderStore.checkIfAllUploadsCompleted()) {
      Modal.error({ title: 'Uploads have not completed yet, please try again.' })
      return
    }
    try {
      setSubmitting(true)
      await action(stateChanges, uploaderStore)
      message.success(successMessage)
    } catch (err) {
      const backendMessage = err?.response?.data?.error || err?.message || errorMessage
      message.error({
        content: backendMessage,
        duration: 8,
      })
      if (backendMessage === oldOrderVersionErrorMessage && selectedOrder) {
        trackEvent(Events.VENDOR_SAVE_OLD_ORDER_VERSION, location.pathname.slice(1), {
          order_id: selectedOrder?.id,
        })
      }
    } finally {
      setSubmitting(false)
    }
  }

  return { isSubmitting, execute }
}
